<script lang="ts">
	import { EventSlotsApi, type IEventSlot } from '@api/EventSlotsApi';
	import Headline from './Headline.svelte';
	import SlotTicket from './SlotTicket.svelte';
	import { getContext } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { IEventStore } from '@stores/event';
	import { goto } from '$app/navigation';
	import { parseSlotTicket } from '$lib/parser';
	import { formatISO } from 'date-fns';
	import Pagination from './Pagination/Pagination.svelte';
	import EventTicketsFilterBar from './EventTicketsFilterBar.svelte';
	import {
		SORTING_KEY_PARAM_MAP,
		type IEventFilterSelectionsStore
	} from '@stores/eventFilterSelections';
	import EmptyResult from './EmptyResult.svelte';
	import { ShopsApi } from '@api/ShopsApi';
	import { isEqual } from 'lodash-es';
	import SectionHeadline from './SectionHeadline.svelte';
	import CmsEventTicketsExternalRegistration from './CmsEventTicketsExternalRegistration.svelte';

	export let eventId: string;
	export let useExternalRegistrationLink: boolean = false;
	export let externalRegistrationUrl: string;
	export let showHeadline = true;
	let slots: IEventSlot[] = [];
	let currentPage = 1;
	let totalPages = 1;

	let nearbyShopLocationVpnrs: string[] = [];
	let nearbySlots: IEventSlot[] = [];
	let currentNearbySlotsPage = 1;
	let totalNearbySlotsPages = 1;
	let hasMultipleLocations = false;

	const eventStore = getContext<Writable<IEventStore>>(`eventStore`);
	const eventFilterSelections = getContext<Writable<IEventFilterSelectionsStore>>(
		'eventFilterSelectionsStore'
	);

	async function getLocationCount() {
		const slotApi = new EventSlotsApi();
		const locationsResult = await slotApi.options('location.address.city', {
			'event.id': eventId,
			_search_query: $eventFilterSelections.eventTicketsSearchText,
			'event.published_on~lte': formatISO(new Date(), { representation: 'date' }),
			'published_on~lte': formatISO(new Date(), { representation: 'date' }),
			'event.restrict_visibility': false,
			...($eventFilterSelections.eventSlotDateRange.start
				? { 'starts_at~gte': formatISO($eventFilterSelections.eventSlotDateRange.start) }
				: {}),
			...($eventFilterSelections.eventSlotDateRange.end
				? { 'starts_at~lte': formatISO($eventFilterSelections.eventSlotDateRange.end) }
				: {})
		});

		if (locationsResult.isOk()) {
			hasMultipleLocations = locationsResult.value.data?.length > 1;
		}
	}

	function getEventSlotBaseParams() {
		return {
			'event.id': eventId,
			_search_query: $eventFilterSelections.eventTicketsSearchText,
			'registration_deadline~gte': formatISO(new Date()),
			'event.published_on~lte': formatISO(new Date(), { representation: 'date' }),
			'starts_at~gt': formatISO(new Date()),
			...SORTING_KEY_PARAM_MAP[$eventFilterSelections.sorting],
			'location.address.city': $eventFilterSelections.locations,
			'event.topic': $eventFilterSelections.topics,
			'event.event_type': $eventFilterSelections.eventTypes,
			'event.target_group': $eventFilterSelections.targetGroups,
			'published_on~lte': formatISO(new Date(), { representation: 'date' }),
			...($eventFilterSelections.eventSlotDateRange.start
				? { 'starts_at~gte': formatISO($eventFilterSelections.eventSlotDateRange.start) }
				: {}),
			...($eventFilterSelections.eventSlotDateRange.end
				? { 'starts_at~lte': formatISO($eventFilterSelections.eventSlotDateRange.end) }
				: {}),
			_limit: 1000,
			_per_page: 5
		};
	}
	async function getEventSlots(page: number) {
		const slotApi = new EventSlotsApi();

		$eventStore.loadingState = true;
		const result = await slotApi.list({
			...getEventSlotBaseParams(),
			_page: page
		});
		$eventStore.loadingState = false;

		if (result.isOk()) {
			slots = result.value.data || [];
			totalPages = result.value.meta.total_pages;
		}
	}

	async function getNearbyEventSlots(page: number) {
		const slotApi = new EventSlotsApi();

		const result = await slotApi.list({
			...getEventSlotBaseParams(),
			'location.vpnr': nearbyShopLocationVpnrs,
			_page: page
		});

		if (result.isOk()) {
			nearbySlots = result.value.data || [];
			totalNearbySlotsPages = result.value.meta.total_pages;
		}
	}

	function handleSelectSlot(slot: IEventSlot) {
		$eventStore.selectedEventId = eventId;
		$eventStore.selectedSlot = slot;

		goto('/checkout/form');
	}

	async function findNearbyLocations() {
		const shopsApi = new ShopsApi();

		const result = await shopsApi.list({
			_latitude: $eventFilterSelections.userLatitude,
			_longitude: $eventFilterSelections.userLongitude,
			_radius: 50,
			_limit: 200,
			type: 'tsg',
			_fields: ['id', 'vpnr', 'name', 'address'],
			sort_by: 'address.distance',
			closed: [true, false],
			'properties.permanently_closed': false
		});

		if (result.isOk()) {
			nearbyShopLocationVpnrs = result.value.data?.map((shop) => shop.vpnr) || [];
		}
	}

	getLocationCount();

	$: if ($eventFilterSelections) {
		getEventSlots(currentPage);
	}

	$: if ($eventFilterSelections.userLatitude && $eventFilterSelections.userLongitude) {
		findNearbyLocations();
	}
	$: if ($eventFilterSelections && nearbyShopLocationVpnrs) {
		getNearbyEventSlots(currentNearbySlotsPage);
	}

	let filterSnapshot = { ...$eventFilterSelections };
	$: if (!isEqual($eventFilterSelections, filterSnapshot)) {
		currentPage = 1;
		currentNearbySlotsPage = 1;

		filterSnapshot = { ...$eventFilterSelections };
	}
</script>

{#if useExternalRegistrationLink}
	<CmsEventTicketsExternalRegistration url={externalRegistrationUrl} />
{:else}
	<div class="relative" class:content-module-padding={showHeadline} {...$$restProps}>
		<!-- used to optimize position of anchor scroll jump -->
		<div class="absolute top-6 lg:top-16 w-full opacity-0" id="termine" />
		{#if showHeadline}
			<SectionHeadline class="!mb-1.5">Termine</SectionHeadline>
			<p class="mb-6 lg:mb-10.5">
				{#if hasMultipleLocations}
					Dieses Event findet an mehreren Standorten statt.
				{/if}
			</p>
		{/if}

		<div class="lg:pl-9">
			<EventTicketsFilterBar {eventId} />

			{#if $eventFilterSelections.userLatitude && $eventFilterSelections.userLongitude}
				<Headline tag="h3" size="quaternary" class="font-normal mb-3">In Ihrer Nähe</Headline>

				<Pagination bind:currentPage={currentNearbySlotsPage} pageCount={totalNearbySlotsPages}>
					<div class="flex flex-col gap-3">
						{#if nearbySlots.length}
							{#each nearbySlots as slot (slot)}
								<SlotTicket on:action={() => handleSelectSlot(slot)} {...parseSlotTicket(slot)} />
							{/each}
						{:else}
							<EmptyResult type="slots" />
						{/if}
					</div>
				</Pagination>
			{/if}

			{#if $eventFilterSelections.userLatitude && $eventFilterSelections.userLongitude}
				<Headline tag="h3" size="quaternary" class="font-normal mt-8 mb-3"
					>Weitere Termine & Standorte</Headline
				>
			{/if}
			<Pagination bind:currentPage pageCount={totalPages}>
				<div class="flex flex-col gap-3">
					{#if slots.length}
						{#each slots as slot (slot)}
							<SlotTicket on:action={() => handleSelectSlot(slot)} {...parseSlotTicket(slot)} />
						{/each}
					{:else}
						<EmptyResult type="slots" />
					{/if}
				</div>
			</Pagination>
		</div>
	</div>
{/if}
